import QRCode from 'react-qr-code'
import { APP_CONSTANTS, DEFAULT_PRINT_FOOTER_SETTINGS } from 'src/consumer/constants'

/**
 * Compact print-footer strip driven by the per-template footer field toggles
 * (footer.printFooterSettings). Renders only the enabled items and auto-sizes to its
 * content (no fixed height, no border line above — the caller renders this standalone for
 * the "config" footer mode).
 *
 * Layout mirrors the e-prescription convention:
 *   LEFT  — disclaimer, page number, prescription id, approval line, AUMHUM logo
 *   RIGHT — QR code + a right-aligned signing block (blank space + signature line + name),
 *           so the practitioner can sign above their printed name.
 */
function formatApprovalDate(raw) {
  if (!raw) return ''
  try {
    const d = new Date(typeof raw === 'number' ? raw : Date.parse(raw))
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return ''
  }
}

export default function PrintFooterMetaBar({
  prescriptionData,
  doctorName,
  currentPage,
  totalPages,
}) {
  const settings = {
    ...DEFAULT_PRINT_FOOTER_SETTINGS,
    ...(prescriptionData?.footer?.printFooterSettings || {}),
  }

  const anyEnabled = Object.values(settings).some(Boolean)
  if (!anyEnabled) return null

  const inner = prescriptionData?.prescriptionData || {}
  const resolvedName =
    doctorName ||
    prescriptionData?.doctorName ||
    prescriptionData?.signatureDetails?.doctorName ||
    inner?.doctorName ||
    ''
  const prescriptionId = inner?.prescriptionId || prescriptionData?.prescriptionId || ''
  const approvalDate = formatApprovalDate(
    inner?.approvedOn || inner?.signedOn || inner?.authorizedOn || inner?.modifiedTime || inner?.createdTime,
  )

  const hasPageNumber =
    settings.showPageNumber &&
    currentPage !== null &&
    currentPage !== undefined &&
    totalPages !== null &&
    totalPages !== undefined

  // QR is generated client-side so it always renders (in preview and print) without
  // depending on a server-side image. Encode the prescription id (scannable reference);
  // fall back to a stable label so a QR still renders in the sample preview.
  const qrValue = prescriptionId || 'AUMHUM Prescription'

  // LEFT items — rendered inline (on one line, separated by a subtle dot) rather than
  // stacked one below the other.
  const leftItems = []
  if (settings.showNotValidForMedicalPurpose) {
    leftItems.push(
      <span key="notvalid" style={{ color: '#64748b' }}>
        Not valid for medico legal purpose
      </span>,
    )
  }
  if (hasPageNumber) {
    leftItems.push(
      <span key="page" style={{ color: '#334155', fontWeight: 700 }}>
        {`Page ${currentPage} of ${totalPages}`}
      </span>,
    )
  }
  if (settings.showPrescriptionId && prescriptionId) {
    leftItems.push(
      <span key="rxid" style={{ color: '#64748b' }}>
        {prescriptionId}
      </span>,
    )
  }
  if (settings.showApprovalDetails && resolvedName) {
    leftItems.push(
      <span key="approval" style={{ color: '#64748b' }}>
        {`Authorized by ${resolvedName}${approvalDate ? ` on ${approvalDate}` : ''}`}
      </span>,
    )
  }

  const showLogo = settings.showAumhumLogo
  const showQr = settings.showQr
  const showName = settings.showName && Boolean(resolvedName)
  const registrationNumber = (settings.registrationNumber || '').toString().trim()
  const showRegistration = settings.showRegistrationNumber && Boolean(registrationNumber)

  const hasLeft = leftItems.length > 0 || showLogo
  const hasRight = showQr || showName
  if (!hasLeft && !hasRight) return null

  return (
    <div
      data-print-footer-meta-bar="true"
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: '16px',
        width: '100%',
        fontSize: '9px',
        lineHeight: 1.35,
        boxSizing: 'border-box',
        padding: '2px 0',
      }}
    >
      {/* LEFT: logo + disclaimer · page · id · approval — all on one line, wrapping if needed */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          columnGap: '8px',
          rowGap: '2px',
          minWidth: 0,
        }}
      >
        {showLogo && (
          <img
            src={APP_CONSTANTS.APP_LOGO_LINK}
            alt="AUMHUM"
            crossOrigin="anonymous"
            style={{ height: '16px', width: 'auto', objectFit: 'contain', display: 'block' }}
          />
        )}
        {leftItems.map((item, i) => (
          <span key={`li-${i}`} style={{ display: 'inline-flex', alignItems: 'center', columnGap: '8px' }}>
            {item}
            {i < leftItems.length - 1 && <span style={{ color: '#cbd5e1' }}>·</span>}
          </span>
        ))}
      </div>

      {/* RIGHT: QR + signing block */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '14px', flexShrink: 0 }}>
        {showQr && (
          <div style={{ width: '54px', height: '54px', flexShrink: 0 }}>
            <QRCode
              value={qrValue}
              size={54}
              style={{ height: '54px', width: '54px' }}
              viewBox="0 0 54 54"
            />
          </div>
        )}
        {showName && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            {/* name is bold; no signature line above it */}
            <div style={{ fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap' }}>
              {resolvedName}
            </div>
            {showRegistration && (
              <div style={{ color: '#475569', whiteSpace: 'nowrap', marginTop: '1px' }}>
                {`Reg. No. ${registrationNumber}`}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
